/**
 * Chip family — constants. Const-asserted source of truth for variants,
 * sizes, and geometry. Adding/removing a value here is enough — dependent
 * types, theme-token maps, Storybook controls, and test iterations pick
 * up the change at compile time.
 *
 * Names mirror Figma exactly (display value → kebab-case for multi-word).
 *
 * Per-parent constants are organised by section. `InteractiveChip`
 * additions land alongside that parent in a later PR.
 */

// ─── Shared geometry ────────────────────────────────────────────────────────

/** Corner radius. Figma `chip/details/corner-radius/subtle`. */
export const CHIP_RADIUS_PX = 6;

/** Total avatar inset within the chip height — 6px ring on each side per Figma. */
export const CHIP_AVATAR_INSET_PX = 12;

// ─── StaticChip ──────────────────────────────────────────────────────────────

/** Visual weight of a StaticChip. Mirrors Figma `Type`. */
export const STATIC_CHIP_TYPES = ['filled', 'outline', 'transparent'] as const;
export type StaticChipType = (typeof STATIC_CHIP_TYPES)[number];

/** Semantic color of a StaticChip. Mirrors Figma `Variant`. */
export const STATIC_CHIP_VARIANTS = [
  'primary',
  'accent',
  'neutral',
  'success',
  'high-alert',
  'medium-alert',
  'error',
  'light',
  'subtle-attention',
] as const;
export type StaticChipVariant = (typeof STATIC_CHIP_VARIANTS)[number];

/** Pixel-height tier of a StaticChip. Mirrors Figma `Size` (Lg / Md / Sm). */
export const STATIC_CHIP_SIZES = ['lg', 'md', 'sm'] as const;
export type StaticChipSize = (typeof STATIC_CHIP_SIZES)[number];

/** Heights per Figma tokens `chip/details/height/{sm,xs,xxs}` for Lg/Md/Sm. */
export const STATIC_CHIP_HEIGHT_PX: Readonly<Record<StaticChipSize, number>> = {
  lg: 28,
  md: 24,
  sm: 20,
};

/** Horizontal padding per Figma tokens `chip/details/horizontal-padding/{base,sm}`. */
export const STATIC_CHIP_PADDING_X_PX: Readonly<Record<StaticChipSize, number>> = {
  lg: 8,
  md: 6,
  sm: 6,
};

/** Gap between LeadIcon/Label/TrailIcon. Figma `chip/details/space-between/horizontal/xs`. */
export const STATIC_CHIP_GAP_PX = 2;

/**
 * Lead/Trail icon box size per StaticChip size. Mirrors Figma
 * `chip/details/asset-size/{base,sm,xs}` — solid 16/12/12 for lg/md/sm so
 * lead glyphs match the chip body weight visually.
 */
export const STATIC_CHIP_ICON_PX: Readonly<Record<StaticChipSize, number>> = {
  lg: 16,
  md: 12,
  sm: 12,
};

// ─── InteractiveChip ─────────────────────────────────────────────────────────

/** Semantic color of an InteractiveChip. Mirrors Figma `Variant`. */
export const INTERACTIVE_CHIP_VARIANTS = ['primary', 'neutral', 'error'] as const;
export type InteractiveChipVariant = (typeof INTERACTIVE_CHIP_VARIANTS)[number];

/** Pixel-height tier of an InteractiveChip. Mirrors Figma `Size` (Lg / Md / Sm). */
export const INTERACTIVE_CHIP_SIZES = ['lg', 'md', 'sm'] as const;
export type InteractiveChipSize = (typeof INTERACTIVE_CHIP_SIZES)[number];

/**
 * Interaction state of an InteractiveChip. Mirrors Figma `State`.
 *
 * `resting` / `hover` are CSS-derived (`:hover`, `:focus-visible`); only
 * `selected` is dev-controlled via the `selected` prop on `InteractiveChip`.
 */
export const INTERACTIVE_CHIP_STATES = ['resting', 'hover', 'selected'] as const;
export type InteractiveChipState = (typeof INTERACTIVE_CHIP_STATES)[number];

/** Heights per Figma tokens `chip/interactive/height/{lg,md,sm}`. */
export const INTERACTIVE_CHIP_HEIGHT_PX: Readonly<Record<InteractiveChipSize, number>> = {
  lg: 40,
  md: 32,
  sm: 28,
};

/** Horizontal padding. Figma `chip/interactive/padding/horizontal` — constant per spec. */
export const INTERACTIVE_CHIP_PADDING_X_PX = 12;

/** Gap between internal slots. Figma `chip/interactive/gap`. */
export const INTERACTIVE_CHIP_GAP_PX = 8;

/**
 * Lead-icon and Delete-icon box size per InteractiveChip size. Mirrors Figma
 * `chip/interactive/asset-size/{lg,md,sm}` and scales with chip height so
 * icons stay visually balanced against the chip's footprint.
 */
export const INTERACTIVE_CHIP_ICON_PX: Readonly<Record<InteractiveChipSize, number>> = {
  lg: 16,
  md: 12,
  sm: 12,
};

/**
 * Font sizes per InteractiveChip size — Figma typography slot.
 * Lg 14/20lh, Md 13/18lh, Sm 12/16lh.
 */
export const INTERACTIVE_CHIP_FONT_PX: Readonly<
  Record<InteractiveChipSize, { fontSize: number; lineHeight: number }>
> = {
  lg: { fontSize: 14, lineHeight: 20 },
  md: { fontSize: 13, lineHeight: 18 },
  sm: { fontSize: 12, lineHeight: 16 },
};

/**
 * InteractiveChipVariant → IndicatorVariant. `Indicator` names its red variant
 * `alert` instead of `error`; everything else aligns 1:1.
 */
export const INTERACTIVE_CHIP_INDICATOR_VARIANT: Readonly<
  Record<InteractiveChipVariant, 'primary' | 'neutral' | 'alert'>
> = {
  primary: 'primary',
  neutral: 'neutral',
  error: 'alert',
};
